import React, { useEffect, useState, useCallback, useRef } from "react";
import { BiSidebar } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import Tabbar from "../base/Tabbar";
import { DataState } from "../../context/DataProvider";
import {
  IoLogOutOutline,
  IoSettingsOutline,
  IoMenu,
  IoClose,
} from "react-icons/io5";
import Modal from "../Modal";
import { IoBulbOutline } from "react-icons/io5";
import { BsFolder2Open, BsQuestionCircle } from "react-icons/bs";
import { IoIosArrowDown } from "react-icons/io";
import { API_URL } from "../../services/api";
import { pages } from "@microsoft/teams-js"; // ✅ Required
import { useTeams } from "../../context/TeamsProvider";
import ActivityIndicator from "./ActivityIndicator"; // Import the ActivityIndicator component
import ProfileSettingsModal from "../ProfileSettingsModal";
const TEAMS_APP_ID = process.env.REACT_APP_TEAMS_APP_ID

const BaseLayout = ({
  leftSide,
  children,
  tabBarAbsolute = true,
  createName,
  chatHistory,
  selectedChatHistory,
  handleSelectChat,
  handleCreate,
  hideLeftHeader = false,
  tabName
}) => {
  const navigate = useNavigate();
  const { isTeamsInitialized } = useTeams();
  const {
    theme,
    setTheme,
    feedbackModalOpen,
    setFeedbackModalOpen,
    justChangedLocalStorageData,
    setJustChangedLocalStorageData,
    toggleSidebar: sidebarCollapsed,
    setToggleSidebar, userInfo,
    rightSidebarWidth, setRightSidebarWidth
  } = DataState();
  const [openSettings, setOpenSettings] = useState(false);
  // const [toggleSidebar, setToggleSidebar] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const toggleSidebar = tabName === "communication" ? true : sidebarCollapsed;

  // --- State and Refs for Resizable Sidebar ---

  const isResizing = useRef(false);
  const resizableContainerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize'; // Change cursor for the whole page
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing.current || !resizableContainerRef.current) return;
    const containerRect = resizableContainerRef.current.getBoundingClientRect();

    // The handle itself is 8px wide (w-2). To make the drag feel centered,
    // we offset the calculation by half the handle's width.
    const handleWidth = 8;
    const newWidth = containerRect.right - e.clientX - (handleWidth / 2);

    if (newWidth >= 200 && newWidth <= 500) {
      setRightSidebarWidth(newWidth);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.body.style.cursor = 'default'; // Reset cursor
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const handleProjectsButtonClick = () => {
    if (!isTeamsInitialized) {
      navigate("/projects");
      return;
    }

    const targetPageId = "projects";
    // pages.currentApp.navigateTo({
    //   pageId: tab.entityId,
    //   // webUrl: `${window.location.origin}${tab.link}`,
    // });
    if (pages.currentApp.isSupported()) {
      pages.currentApp.navigateTo({
        pageId: targetPageId,
        // subPageId: targetSubPageId, // Use if you need to pass internal state/route
      }).then(() => {
        console.log(`Successfully navigated to pageId: ${targetPageId} using currentApp.navigateTo`);
      }).catch((error) => {
        console.error(`Error navigating to pageId: ${targetPageId} using currentApp.navigateTo:`, error);
      });
    } else {
      console.warn("pages.currentApp.navigateTo is not supported, attempting fallback with pages.navigateToApp");
      pages.navigateToApp({
        appId: TEAMS_APP_ID,
        pageId: targetPageId,
        // subPageId: targetSubPageId,
      }).then(() => {
        console.log(`Successfully navigated to pageId: ${targetPageId} using navigateToApp`);
      }).catch((error) => {
        console.error(`Error navigating to pageId: ${targetPageId} using navigateToApp:`, error);
        // As a last resort, if Teams navigation fails, you might consider a React Router navigation,
        // but this won't change the Teams tab.
        // navigate(`/${targetPageId}`);
      });
    }
  }

  const handleLogout = () => {
    async function logout() {
      try {
        const response = await fetch(`${API_URL}/logout`, {
          method: "POST",
          credentials: "include", // 🔥 important! send cookies with the request
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();

        if (response.ok) {
          console.log("Logout successful:", data.message);
        } else {
          console.error("Logout failed:", data.detail || "Unknown error");
        }
      } catch (error) {
        console.error("Network error during logout:", error);
      } finally {
        localStorage.clear();
        setJustChangedLocalStorageData(true);
        navigate("/login");
      }
    }
    logout();
  };

  const handleThemeChange = (e) => {
    setTheme(e.target.value);
  };

  useEffect(() => {
    const storedUserGoogleId = localStorage.getItem("projectToken");
    if (!storedUserGoogleId) {
      navigate("/login");
    }
  }, [navigate]);

  const SidebarContent = () => (
    <div className="flex flex-col justify-between h-full gap-1 overflow-hidden bg-1 rounded-2xl">
      <div className="relative flex flex-col items-center gap-0 px-2 py-0 ">
        {/* Mobile Header with Close Button */}
        <div className="flex items-center justify-between w-full mb-2 sm:hidden">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMobileSidebarOpen(false);
            }}
            className="absolute top-0 right-0 p-1 transition-colors rounded-lg hover:bg-white hover:bg-opacity-10"
          >
            <IoClose className="text-xl" />
          </button>
        </div>
        {/* <img
          src={theme === "light" ? "../assets/logo.png" : "../assets/logo.png"}
          alt=""
          className="w-full h-8 mb-2 text-center md:mb-4 md:h-10"
        /> */}
        {/* Logo Container */}
        <div className="flex items-center justify-center w-full gap-0 mb-2 md:mb-4">
          {/* Main Logo (Always visible) */}
          {/* <img
            src={`../assets/logo.svg`}
            alt="Main Logo"
            className="object-contain h-8 md:h-10"
          /> */}

          {/* Partner Logo (Conditionally renders if it exists) */}
          {userInfo?.partnerLogo && !toggleSidebar && (
            <>
              {/* Visual Separator for clarity */}
              <div className="h-6 border-l border-white/30" aria-hidden="true"></div>

              <img
                src={`../assets/partners/${userInfo.partnerLogo}`}
                alt="Partner Logo"
                className="object-contain h-7 md:h-9"
              />
            </>
          )}
        </div>
        <div className="flex flex-col w-full gap-4">
          <button
            onClick={handleCreate}
            className={`flex justify-center items-center gap-2 
              rounded-xl px-3 py-3 md:py-2 cursor-pointer bg-primary-gradient 
              text-white text-sm md:text-xs sm:text-sm transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:pointer-events-none 
              ${toggleSidebar && "font-bold text-xl"}`}
          >
            {toggleSidebar ? "+" : createName}
          </button>

          {chatHistory && !toggleSidebar && (
            <button
              className="flex items-center justify-between gap-1 px-2 font-semibold"
              onClick={() => setShowHistory(!showHistory)}
            >
              Recent Chats{" "}
              <IoIosArrowDown
                className={`${showHistory ? "rotate-180" : ""}`}
              />
            </button>
          )}
        </div>
      </div>

      {chatHistory && !toggleSidebar && showHistory && (
        <div className="flex flex-col flex-1 gap-2 p-3 mx-2 -mt-3 overflow-y-auto hide-sidebar rounded-xl bg-2">
          {chatHistory.length > 0 ? (
            chatHistory.map((task) => (
              <div
                key={task._id}
                className="relative"
                onClick={() => handleSelectChat(task)}
              >
                <div
                  className={`${task._id === selectedChatHistory
                    ? "bg-white bg-opacity-15"
                    : "bg-white bg-opacity-5"
                    } flex items-center gap-2 rounded-lg hover:bg-zinc-800 px-3 py-1 cursor-pointer transition-all duration-200`}
                >
                  <p className="overflow-hidden text-sm text-ellipsis whitespace-nowrap">
                    {task.chatName}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col gap-3 p-2 text-center">
              No recent chats
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col w-full gap-1 px-2 py-3 text-s-text">
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpenSettings(true);
            // Close sidebar on mobile
            if (window.innerWidth < 768) {
              setMobileSidebarOpen(false);
            }
          }}
          className={`w-full rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"} items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          {userInfo && (
            <div className="flex items-center gap-3">
              {userInfo.picture ? (
                <img
                  src={userInfo.picture}
                  alt={userInfo.name}
                  className="rounded-full h-[25px] w-[25px] object-cover"
                />
              ) : (
                <div className="rounded-full h-[25px] w-[25px] bg-gray-300 flex items-center justify-center text-sm font-bold text-gray-600">
                  {userInfo.name ? userInfo.name?.charAt(0).toUpperCase() : userInfo.email?.charAt(0).toUpperCase()}
                </div>
              )}
              <span
                className={`max-w-20 md:max-w-24 overflow-hidden text-ellipsis whitespace-nowrap text-sm ${toggleSidebar && "hidden"}`}
              >
                {userInfo.name || userInfo.email}
              </span>
            </div>
          )}
        </button>
        {/* <button
          className={`rounded-lg gap-3 flex ${
            toggleSidebar ? "justify-center" : "justify-start"
          } items-center p-2`}
        >
          <IoSettingsOutline className="text-2xl" />
          {!toggleSidebar && "Settings"}
        </button> */}

        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleProjectsButtonClick();
          }}
          className={`rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <BsFolder2Open className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Projects</span>
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleLogout();
          }}
          className={`rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <IoLogOutOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Log out</span>
          )}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setFeedbackModalOpen(true);
            // Close sidebar on mobile

          }}
          className={`rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <IoBulbOutline className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && (
            <span className="text-sm md:text-sm">Feedback</span>
          )}
        </button>

        <a
          href="mailto:nirmalnambiar@oqlous.com"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.location.href = "mailto:nirmalnambiar@oqlous.com";
          }}
          className={`rounded-lg gap-3 flex ${toggleSidebar ? "justify-center" : "justify-start"
            } items-center p-3 md:p-2 hover:bg-white hover:bg-opacity-10 transition-all duration-200`}
        >
          <BsQuestionCircle className="flex-shrink-0 text-xl md:text-2xl" />
          {!toggleSidebar && <span className="text-sm md:text-sm">Help</span>}
        </a>
      </div>

      <ProfileSettingsModal
        isOpen={openSettings}
        onClose={() => setOpenSettings(false)}
        userInfo={userInfo}
      />
    </div>
  );

  return (
    <div className="relative h-screen w-screen max-h-[100svh] bg-3 flex flex-col md:flex-row p-2 pt-4 md:p-2 gap-2 md:gap-2 text-p-text overflow-hidden">
      {/* Desktop Sidebar */}
      <div
        className={`${toggleSidebar ? "hidden md:block md:w-16" : "hidden md:block md:w-56"
          } h-full relative overflow-hidden transform transition-all duration-150 flex-shrink-0`}
      >
        {!toggleSidebar && !userInfo?.partnerLogo && (
          <button
            className="absolute z-10 p-1 text-xl transition-colors rounded-lg top-2 left-2 md:text-2xl hover:bg-white hover:bg-opacity-10"
            onClick={() => setToggleSidebar(!toggleSidebar)}
          >
            <BiSidebar />
          </button>
        )}
        <SidebarContent />
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-[200] bg-black bg-opacity-50 sm:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        >
          <div
            className="w-64 h-full p-4 bg-1"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarContent />
          </div>
        </div>
      )}

      {/* NEW: Wrapper for the main resizable area */}
      <div ref={resizableContainerRef} className="flex flex-1 h-full min-w-0">


        {/* Main Content */}
        <div className="relative flex flex-col flex-1 h-full p-2 overflow-hidden border bg-1 rounded-2xl sm:p-4 gap-7 border-zinc-700">
          {tabName !== "communication" && (toggleSidebar || userInfo?.partnerLogo) && (
            <button
              className="absolute z-10 hidden p-1 text-lg transition-colors rounded-lg top-2 left-2 md:text-xl md:block hover:bg-white hover:bg-opacity-10"
              onClick={() => setToggleSidebar(!toggleSidebar)}
            >
              <BiSidebar />
            </button>
          )}
          {/* Mobile Sidebar Toggle Button */}
          <button
            className="absolute z-30 text-2xl top-2 left-2 sm:hidden"
            onClick={() => {
              setMobileSidebarOpen(true);
              setToggleSidebar(false);
            }}
          >
            <IoMenu />
          </button>
          <Tabbar absolute={tabBarAbsolute} />
          {children}
        </div>
        {leftSide && (
          <>
            {/* MODIFIED: Draggable Handle - This now defines the separation space */}
            <div
              className="items-center justify-center flex-shrink-0 hidden w-2 md:flex cursor-col-resize group"
              onMouseDown={handleMouseDown}
            >
              <div className="w-0.5 h-12 bg-zinc-700 group-hover:bg-[#5652CF] rounded-full transition-colors duration-200" />
            </div>

            <div className="hidden h-full overflow-hidden md:block" style={{ width: `${rightSidebarWidth}px` }} >
              <div className="flex flex-col h-full border shadow-lg bg-1 rounded-xl border-zinc-800">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b h-14 border-zinc-700">
                  <h1 className="text-sm font-bold tracking-wide text-zinc-200">Workspace</h1>
                  {/* {(
                <div className="flex items-center gap-2 px-3 py-1 border rounded-full bg-blue-900/50 border-blue-700/60">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-blue-300">Running</span>
                </div>
              )} */}
                  <ActivityIndicator />
                </div>
                {/* Conditionally render the heading */}
                {/* {!hideLeftHeader && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h1 className="text-base font-bold ">Oqlous Workspace</h1>
                </div>
              </>
            )} */}

                {/* <div
              className={`flex flex-col gap-3 rounded-3xl flex-1 overflow-hidden ${hideLeftHeader ? "" : "bg-2"
                }`}
            > */}
                {/* {!hideLeftHeader && (
                <div className="flex items-center justify-between w-full px-5 py-3 border-b br-1 border-opacity-20">
                 
                  <div className="flex items-center gap-3 ">
                  <div className="bg-[#00A01C] h-3 w-3 rounded-full hover:scale-105"></div>
                  <span className="text-base">Planning</span>
                </div>
                </div>
              )} */}
                {leftSide}
                {/* </div> */}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default BaseLayout;
